import { useMutation, useQuery } from '@apollo/client/react';
import { App } from 'antd';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { v4 as uuidv4 } from 'uuid';
import { MainContent } from '../../components/MainContent';
import { LazySheetEditor } from '../../components/sheet-editor/LazySheetEditor.tsx';
import type { SheetDataWithId } from '../../components/sheet-editor/types';
import type { SheetData } from '../../graphql/sheet';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { hasFrontDeskRole } from '../../utils/user.ts';
import { DOCTOR_SHEET_QUERY, SAVE_DOCTOR_SHEET_MUTATION } from './Sheets.gql';

export const Sheets: React.FC = () => {
  const { message } = App.useApp();
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const [changedSheetData, setChangedSheetData] = useState<SheetDataWithId[] | null>(null);
  const auth = useAuth();

  const { data, loading } = useQuery(DOCTOR_SHEET_QUERY, {
    variables: {
      doctorId: hasFrontDeskRole(auth.user) ? primaryDoctorId : currentDoctorId,
    },
  });

  const sheetData = useMemo(() => {
    // user manipulated the data
    if (changedSheetData) {
      return changedSheetData;
    }

    // initial data from API
    const [sheet] = data?.doctorSheets ?? [];
    if (sheet) {
      return (sheet.data as SheetData[]).map(sheet => ({
        ...sheet,
        id: uuidv4(),
        groups: sheet.groups.map(group => ({
          ...group,
          id: uuidv4(),
          parameters: group.parameters.map(param => ({
            ...param,
            paramId: param.id,
            id: uuidv4(),
          })),
        })),
      }));
    }

    // API loading
    return [];
  }, [changedSheetData, data]);

  const [saveDoctorSheetMutation] = useMutation(SAVE_DOCTOR_SHEET_MUTATION);

  const save = async (data: SheetDataWithId[]) => {
    try {
      await saveDoctorSheetMutation({
        variables: {
          doctorId: hasFrontDeskRole(auth.user) ? primaryDoctorId : currentDoctorId,
          input: {
            data: data.map(it => ({
              name: it.name,
              groups: it.groups.map(it => ({
                name: it.name,
                parameterIds: it.parameters.map(it => it.paramId),
              })),
            })),
          },
        },
      });
    } catch {
      message.error('Das Sheet konnte nicht gespeichert werden');
    }
  };

  const autoSave = (sheets: SheetDataWithId[]) => {
    setChangedSheetData(sheets);
    save(sheets);
  };

  return (
    <MainContent size="medium">
      <LazySheetEditor
        title="Sheets"
        loading={loading}
        sheets={sheetData}
        onChange={autoSave}
        showLab
        flipParamNames={data?.doctor?.flipParamNames ?? false}
        parameters={data?.catalogParameters?.map(it => it.parameter) ?? []}
      />
    </MainContent>
  );
};
